import React, { useContext, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { AppDivisionContext } from '../../../contexts/AppDivisionContext';
import { InspectionContext } from '../../../contexts/InspectionContext';
import { ProjectContext } from '../../../contexts/ProjectContext';
import RolesContext from '../../../contexts/RolesContext';
import { assetStatusesApi } from '../../../utils/api/asset';
import { projectAssetsApi } from '../../../utils/api/projectAsset';
import AssetCard from '../../AssetTypes/AssetCard/AssetCard';
import CreateAssetModal from '../../Projects/Details/Assets/CreateAssetModal';
import Loading from '../../Shared/ResourceIndex/Loading';

/**
 * Assets shown inside an inspection. Lists the project assets that belong to
 * this inspection's template (filtered server-side via `inspection_id`) and
 * lets the inspector fill the per-inspection fields, notes and photos.
 */
const InspectionAssets: React.FC = () => {
  const permCheck = useContext(RolesContext).userHasPermission;
  const { appDivisionId } = useContext(AppDivisionContext);
  const projectContext = useContext(ProjectContext);
  const inspectionContext = useContext(InspectionContext);
  const queryClient = useQueryClient();

  const projectId = projectContext.project?.id;
  const inspectionId = inspectionContext.inspection?.id;
  const isSigned =
    !!inspectionContext.inspection?.certification_date ||
    !!inspectionContext.inspection?.compliance_date ||
    inspectionContext.isInspectionReadOnly;
  const canEdit = !isSigned && permCheck('update', 'Inspection');
  const canCreate = canEdit && permCheck('create', 'Project');
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');
  const [statusId, setStatusId] = useState<number | 'all'>('all');
  const [archiveFilter, setArchiveFilter] = useState<'active' | 'archived'>(
    'active',
  );

  const assetsKey = ['projectAssets', projectId, 'inspection', inspectionId];

  const assetsQuery = useQuery(
    assetsKey,
    () => projectAssetsApi.index(projectId!, inspectionId),
    { enabled: !!projectId && !!inspectionId },
  );
  const divisionId = appDivisionId || 0;
  const statusesQuery = useQuery(['assetStatuses', divisionId], () =>
    assetStatusesApi.index(divisionId),
  );

  const invalidate = () => queryClient.invalidateQueries(assetsKey);

  const statuses = statusesQuery.data || [];

  const filtered = useMemo(() => {
    const assets = assetsQuery.data || [];
    const term = search.trim().toLowerCase();

    return assets.filter((a) => {
      const matchesArchive =
        archiveFilter === 'archived'
          ? !!a.archived_for_inspection
          : !a.archived_for_inspection;
      const matchesStatus =
        statusId === 'all' || a.asset_status_id === statusId;
      const matchesSearch =
        !term ||
        a.asset_name.toLowerCase().includes(term) ||
        (a.asset_type?.name || '').toLowerCase().includes(term);

      return matchesArchive && matchesStatus && matchesSearch;
    });
  }, [assetsQuery.data, search, statusId, archiveFilter]);

  return (
    <div className='project-assets'>
      <div className='project-assets__toolbar'>
        <input
          className='project-assets__search'
          type='search'
          placeholder='Search assets…'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className='project-assets__filter'
          value={statusId}
          onChange={(e) =>
            setStatusId(
              e.target.value === 'all' ? 'all' : Number(e.target.value),
            )
          }
        >
          <option value='all'>All statuses</option>
          {statuses.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>

        <div className='project-assets__archive-toggle'>
          <button
            type='button'
            className={archiveFilter === 'active' ? 'is-active' : ''}
            onClick={() => setArchiveFilter('active')}
          >
            Active
          </button>
          <button
            type='button'
            className={archiveFilter === 'archived' ? 'is-active' : ''}
            onClick={() => setArchiveFilter('archived')}
          >
            Archived
          </button>
        </div>

        <span className='spacer' />

        {canCreate && (
          <button
            className='primary project-assets__create'
            onClick={() => setShowCreate(true)}
          >
            + Create Asset
          </button>
        )}
      </div>

      {showCreate && (
        <CreateAssetModal
          projectId={projectId!}
          divisionId={divisionId}
          lockedInspectionTemplateId={inspectionContext.inspection?.template_id}
          onSaved={() =>
            queryClient.invalidateQueries(['projectAssets', projectId])
          }
          onClose={() => setShowCreate(false)}
        />
      )}

      {isSigned && (
        <p className='project-assets__locked'>
          This inspection has been signed — asset answers are read-only.
        </p>
      )}

      {assetsQuery.isLoading ? (
        <Loading />
      ) : filtered.length === 0 ? (
        <p className='project-assets__empty'>
          No assets to inspect for this inspection type.
        </p>
      ) : (
        filtered.map((asset) => (
          <AssetCard
            key={asset.id}
            projectId={projectId!}
            asset={asset}
            inspectionId={inspectionId}
            canEdit={canEdit}
            onChanged={invalidate}
          />
        ))
      )}
    </div>
  );
};

export default InspectionAssets;
